import { useState } from "react";
import { Button } from "../ui/button";
import { Monitor, MonitorOff } from "lucide-react";
import { useReceiveActionAccess } from "@/hooks/livekit/useReceiveActionAccess";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentRoom, setShare } from "@/context/features/room/roomSlice";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useTranslation } from "react-i18next";

export default function ScreenShareToggle({
  startfunc,
  stopfunc,
}: {
  startfunc: (() => Promise<boolean | undefined>) | undefined;
  stopfunc: (() => Promise<boolean | undefined>) | undefined;
}) {
  const [shareLocked, setShareLocked] = useState<boolean>(false);

  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  const { user } = useSelector(selectCurrentAuth);
  const { isShareOpen } = useSelector(selectCurrentRoom);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useReceiveActionAccess("screen", room, (data) => {
    if (user?.id.toString() === data.to) {
      if (data.type === "lock" && isShareOpen) {
        if (typeof stopfunc === "function") stopfunc();
        dispatch(setShare(false));
      }
      setShareLocked(data.type === "lock");
    }
  });

  const handleClick = async () => {
    if (isShareOpen) {
      if (stopfunc) await stopfunc();
    } else {
      if (startfunc) await startfunc();
    }
    dispatch(setShare(!isShareOpen));
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          onClick={handleClick}
          disabled={shareLocked}
        >
          {isShareOpen ? (
            <Monitor strokeWidth={1.5} />
          ) : (
            <MonitorOff strokeWidth={1.5} />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {shareLocked
          ? t("locked")
          : isShareOpen
            ? t("clsoe_screen")
            : t("open_screen")}
      </TooltipContent>
    </Tooltip>
  );
}
